/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Divider,
  Flex,
  Text,
  useColorModeValue,
  useMediaQuery,
} from '@chakra-ui/react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts'
import { useTranslations } from 'next-intl'
import { BAR_DATA_KEYS } from '@/constants'
import { IBillingHistory } from '@/types/billing.interface'
import { formatInvoiceDate } from '@/utils/date'
import { NoDataText } from '../../NoDataText'
import { formatPrice } from '@/utils/formatPrice'

interface BillingHistoryProps {
  downtimeData: any
  isDrawer?: boolean
}

interface AggregatedData {
  invoiceMonth: number
  invoiceYear: number
  downtimeCredits: number
  downtime: number
  uptime: number
  uptimeCount: number
  finalDate: string
  total: number
  minersInvoiced: number
}

export const BillingHistory = ({
  downtimeData,
  isDrawer = false,
}: BillingHistoryProps) => {
  const t = useTranslations()
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const [selectedPayload, setSelectedPayload] = useState<any>(null)
  const tickColor = useColorModeValue('black', 'white')
  const tickColorDrawer = useColorModeValue('black', '#B1B1B1')
  const gridColor = useColorModeValue(
    'rgba(0, 0, 0, 0.16)',
    'rgba(255, 255, 255, 0.06)'
  )

  const aggregateDataByMonthYear = () => {
    const groupedData = new Map<string, AggregatedData>()

    downtimeData?.forEach((entry: IBillingHistory) => {
      const {
        invoiceMonth,
        invoiceYear,
        downtimeCredits,
        totalMonthlyHostingFee,
        avgUptimePercentage,
        avgDowntimeHoursPercentage,
        minersInvoiced,
      } = entry
      const key = `${invoiceYear}-${invoiceMonth}`

      if (!groupedData.has(key)) {
        groupedData.set(key, {
          invoiceMonth,
          invoiceYear,
          downtimeCredits: 0,
          downtime: 0,
          uptime: 0,
          uptimeCount: 0,
          finalDate: formatInvoiceDate(invoiceMonth, invoiceYear),
          total: 0,
          minersInvoiced: 0,
        })
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const aggregated = groupedData.get(key)!
      aggregated.downtimeCredits += downtimeCredits
      aggregated.uptime += parseFloat(avgUptimePercentage)
      aggregated.uptimeCount += 1
      aggregated.total = totalMonthlyHostingFee
      aggregated.downtime += parseFloat(avgDowntimeHoursPercentage)
      aggregated.minersInvoiced += Number(minersInvoiced)
    })

    groupedData.forEach((aggregated) => {
      if (aggregated.uptimeCount > 0) {
        aggregated.uptime = Math.min(
          ((aggregated.uptime / aggregated.uptimeCount) * aggregated.total) /
            100,
          aggregated.total
        )

        aggregated.downtime = Math.min(
          ((aggregated.downtime / aggregated.uptimeCount) * aggregated.total) /
            100,
          aggregated.total
        )
      }
    })

    return Array.from(groupedData.values()).sort(
      (a, b) => a.invoiceYear - b.invoiceYear || a.invoiceMonth - b.invoiceMonth
    )
  }

  const CustomTooltip = ({ active, payload }: any) => {
    const bg = useColorModeValue('black', '#3A3A3A')

    if (active && payload && payload.length) {
      const data = payload[0].payload

      const month = new Date(
        data.invoiceYear,
        data.invoiceMonth - 1
      ).toLocaleString('en-US', { month: 'long' })

      return (
        <Box
          background={bg}
          color={'white'}
          p={2}
          borderRadius="4px"
          maxW="217px"
          w="217px"
        >
          <Text fontSize="14px" fontWeight="700">{`${month} ${t(
            'dashboard.billing.billing-history-details.performance-breakdown'
          )}`}</Text>
          <Flex align="center" fontSize="14px">
            <Text color="blue.400">
              {`${Math.round((Number(data.uptime) / data.total) * 100)}% ${t(
                'dashboard.billing.billing-history-details.uptime'
              )}`}
            </Text>
            <Text ml={1}>
              {`(${t('dashboard.billing.billing-history-details.sla')})`}
            </Text>
          </Flex>
          <Text fontSize="14px">
            {`${Math.round((Number(data.downtime) / data.total) * 100)}% ${t(
              'dashboard.billing.billing-history-details.downtime'
            )}`}
          </Text>
          <Divider borderColor="gray.600" />
          <Text fontSize="14px">
            {`
              ${data.minersInvoiced} ${t(
              'dashboard.billing.billing-history-details.miners-billed'
            )}
              ${formatPrice(data.total)}
            `}
          </Text>
          <Text fontSize="14px">
            {`${t(
              'dashboard.billing.billing-history-details.credits-issued'
            )}: ${formatPrice(data.downtimeCredits)}`}
          </Text>
        </Box>
      )
    }
    return null
  }
  const chartData = aggregateDataByMonthYear()

  const CustomLegend = ({ payload }: any) => {
    const legendColor = useColorModeValue('black', 'white')

    return (
      <Flex justifyContent="center" gap={3} mt={3}>
        {payload.map((entry: any, index: number) => (
          <Flex key={`legend-${index}`} align="center" gap={2}>
            <Box w="10px" h="10px" borderRadius="full" bg={entry.color} />
            <Text fontSize="14px" color={legendColor}>
              {entry.value}
            </Text>
          </Flex>
        ))}
      </Flex>
    )
  }

  const BottomLabel = (props: any) => {
    const { x, y, value, height, width, data } = props
    const matchingData = data?.find(
      (item: AggregatedData) => item.uptime === value
    )
    return (
      <text
        x={x + width / 2}
        y={y + height - 10}
        fill="white"
        textAnchor="middle"
        fontSize="12px"
        fontWeight="800"
      >
        {Math.round((Number(value) / matchingData?.total) * 100)}%
      </text>
    )
  }

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (
        drawerContentRef.current &&
        !drawerContentRef.current.contains(e.target as Node)
      ) {
        setSelectedPayload(null)
      }
    }
    document.addEventListener('click', handleDocumentClick)
    return () => document.removeEventListener('click', handleDocumentClick)
  }, [])

  return downtimeData?.length > 1 ? (
    <Box
      ref={drawerContentRef}
      onClick={() => {
        setSelectedPayload(null)
      }}
      mx="-32px"
      px="32px"
    >
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          barCategoryGap={isMobile ? (isDrawer ? '25%' : '10%') : '25%'}
        >
          <CartesianGrid stroke={gridColor} />
          <XAxis
            dataKey="finalDate"
            tick={{ fill: isDrawer ? tickColorDrawer : tickColor }}
            axisLine={{ stroke: gridColor }}
            tickSize={0}
            tickMargin={15}
          />
          <YAxis
            width={66}
            tick={{ fill: isDrawer ? tickColorDrawer : tickColor }}
            tickFormatter={(value) => `$${value.toLocaleString('en-US')}`}
            axisLine={{ stroke: gridColor }}
            tickSize={0}
            tickMargin={5}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'transparent' }}
            wrapperStyle={{
              pointerEvents: 'none',
              zIndex: 1000,
            }}
            isAnimationActive={false}
          />
          <Legend content={<CustomLegend />} />
          <Bar
            dataKey={BAR_DATA_KEYS.UPTIME}
            stackId="total"
            name={t('dashboard.billing.billing-history-details.uptime')}
            fill="#4299E1"
            cursor="pointer"
            shape={(props: any) => {
              const { x, y, width, height, payload } = props
              return (
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill="#4299E1"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedPayload(payload)
                  }}
                />
              )
            }}
            label={(props) => <BottomLabel {...props} data={chartData} />}
          />
          <Bar
            dataKey={BAR_DATA_KEYS.DOWNTIME}
            stackId="total"
            name={t('dashboard.billing.billing-history-details.downtime')}
            fill="#4A5568"
            shape={(props: any) => {
              const { x, y, width, height, payload } = props
              return (
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill="#4A5568"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedPayload(payload)
                  }}
                />
              )
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  ) : (
    <NoDataText
      noDataText={t(
        'dashboard.billing.billing-history-details.no-data-description'
      )}
      title={t('dashboard.billing.billing-history-details.not-enough-data')}
      maxWidth="620px"
      textAlign={'center'}
    />
  )
}
